import React, { useMemo, useState } from 'react';
import { Award, Users, Clock, TrendingUp, Search, Trash2, X, AlertTriangle } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import {
  formatValidity,
  Certification,
} from '@/services/AdminService/AdminCertificationService';
import './CertificationList.css';

const CertificationList: React.FC = () => {
  const { certifications, loading, error, deleteCertification } = useCertificationManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    certification: Certification | null;
  }>({ isOpen: false, certification: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredCertifications = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (certifications ?? []).filter((cert) => {
      if (statusFilter !== 'all' && cert.status !== statusFilter) return false;
      if (!term) return true;
      return (
        (cert.certification_name ?? '').toLowerCase().includes(term) ||
        (cert.description ?? '').toLowerCase().includes(term)
      );
    });
  }, [certifications, searchTerm, statusFilter]);

  const handleDeleteClick = (cert: Certification) => {
    setDeleteModal({ isOpen: true, certification: cert });
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModal({ isOpen: false, certification: null });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.certification) return;
    setIsDeleting(true);
    const result = await deleteCertification(deleteModal.certification.id);
    setIsDeleting(false);
    if (result.success) {
      setDeleteModal({ isOpen: false, certification: null });
    }
  };

  // Calculate stats from certifications data
  const totalCertifications = certifications?.length || 0;
  const activeCertifications = certifications?.filter(cert => cert.status === 'active').length || 0;

  const stats = [
    { label: 'Total Certifications', value: totalCertifications.toString(), icon: <Award size={20} />, color: '#3B82F6' },
    { label: 'Active Certifications', value: activeCertifications.toString(), icon: <Users size={20} />, color: '#10B981' },
    { label: 'Expiring Soon', value: '0', icon: <Clock size={20} />, color: '#F59E0B' },
    { label: 'Completion Rate', value: '0%', icon: <TrendingUp size={20} />, color: '#8B5CF6' },
  ];

  if (loading && certifications.length === 0) {
    return (
      <div className="admin-certifi-list">
        <div className="admin-certifi-loading">
          <div className="spinner"></div>
          <p>Loading certifications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-certifi-list">
        <div className="admin-certifi-error">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-certifi-list">
      <div className="admin-certifi-list-header">
        <div className="admin-certifi-stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="admin-certifi-stat-card">
              <div className="admin-certifi-stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="admin-certifi-stat-info">
                <p className="admin-certifi-stat-label">{stat.label}</p>
                <h3 className="admin-certifi-stat-value">{stat.value}</h3>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-certifi-list-controls">
          <div className="admin-certifi-search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search Certificate"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="admin-certifi-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="admin-certifi-certifications-grid">
        {certifications.length === 0 ? (
          <div className="admin-certifi-empty-state">
            <Award size={48} />
            <h3>No Certifications Yet</h3>
            <p>Create your first certification to get started</p>
          </div>
        ) : filteredCertifications.length === 0 ? (
          <div className="admin-certifi-empty-state">
            <Award size={48} />
            <h3>No matches</h3>
            <p>No certifications match your search or filter.</p>
          </div>
        ) : (
          filteredCertifications.map((cert) => (
            <div key={cert.id} className="admin-certifi-cert-card">
              <div className="admin-certifi-cert-card-header">
                <div className="admin-certifi-cert-icon">
                  <Award size={24} />
                </div>
                <span className={`admin-certifi-cert-status admin-certifi-cert-status-${cert.status}`}>
                  {cert.status}
                </span>
              </div>

              <h3 className="admin-certifi-cert-name">{cert.certification_name}</h3>
              <p className="admin-certifi-cert-description">{cert.description || 'No description available'}</p>

              <div className="admin-certifi-cert-details">
                <div className="admin-certifi-cert-detail-item">
                  <span className="admin-certifi-detail-label">Validity:</span>
                  <span className="admin-certifi-detail-value">
                    {formatValidity(cert.validity_type, cert.validity_period)}
                  </span>
                </div>
                <div className="admin-certifi-cert-detail-item">
                  <span className="admin-certifi-detail-label">Requirements:</span>
                  <span className="admin-certifi-detail-value">{cert.required_courses_count || 0} items</span>
                </div>
                <div className="admin-certifi-cert-detail-item">
                  <span className="admin-certifi-detail-label">Created By:</span>
                  <span className="admin-certifi-detail-value">
                    {cert.created_by_name && cert.created_by_lastname
                      ? `${cert.created_by_name} ${cert.created_by_lastname}`
                      : 'N/A'}
                  </span>
                </div>
              </div>

              <div className="admin-certifi-cert-actions">
                <button
                  type="button"
                  className="admin-certifi-action-btn admin-certifi-action-btn-delete"
                  onClick={() => handleDeleteClick(cert)}
                  title="Delete Certification"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>

              <p className="admin-certifi-cert-created">
                Created {new Date(cert.created_at).toLocaleDateString()}
              </p>
            </div>
          ))
        )}
      </div>

      {deleteModal.isOpen && deleteModal.certification && (
        <div className="admin-certifi-modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="admin-certifi-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-certifi-modal-header">
              <div className="admin-certifi-modal-icon-warning">
                <AlertTriangle size={24} />
              </div>
              <button
                className="admin-certifi-modal-close"
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-certifi-modal-body">
              <h3 className="admin-certifi-modal-title">Delete Certification?</h3>
              <p className="admin-certifi-modal-message">
                Are you sure you want to delete{' '}
                <strong>"{deleteModal.certification.certification_name}"</strong>?
              </p>
              <p className="admin-certifi-modal-warning">
                This action cannot be undone. All associated data will be permanently removed.
              </p>
            </div>

            <div className="admin-certifi-modal-footer">
              <button
                className="admin-certifi-modal-btn admin-certifi-modal-btn-cancel"
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="admin-certifi-modal-btn admin-certifi-modal-btn-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="spinner-small"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationList;
